import {
  GalaxyColumnDef,
  PagedListRequestInterface,
  PagedResponseInterface,
  PaginatedAPIInterface,
} from '@vendasta/galaxy/table';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { MeetingBot, PrivacyLevel, PrivacyOverride } from '@vendasta/protos/meeting_analysis/v1';
import { listMeetingBots$ } from '@vendasta/protos/meeting_analysis/v1/meeting-bot-service';
import {
  GalaxyFilterDefinitionInterface,
  GalaxyFilterInputOverride,
  GalaxyFilterInterface,
  GalaxyFilterOperator,
  GalaxyFilterType,
} from '@vendasta/galaxy/filter/chips';
import { inject, Injectable } from '@angular/core';
import {
  MEETING_ANALYSIS_NAMESPACE_INJECTION_TOKEN$,
  MEETING_ANALYSIS_USER_ID_INJECTION_TOKEN$,
} from '@galaxy/meeting-analysis/static';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

export interface UpcomingConversationRow {
  id: string;
  namespace: string;
  url: string;
  title: string;
  date: string;
  meetingDate: Date;
  startTime: string;
  privacyLevel: PrivacyLevel;
  guestCount: number;
  overriddenPrivacy: PrivacyOverride | null;
}

const UpcomingConversationFilters: GalaxyFilterDefinitionInterface[] = [
  {
    fieldId: 'keywords',
    type: GalaxyFilterType.FILTER_TYPE_STRING,
    supportedOperators: [GalaxyFilterOperator.FILTER_OPERATOR_IS_ANY],
    hasFilterOptions: false,
    fieldName: 'Keywords',
  },
];

export const UpcomingColumns: GalaxyColumnDef[] = [
  {
    id: 'title',
    title: 'MEETING_LIST.TABLE.COLUMNS.TITLE',
  },
  {
    id: 'url',
    title: 'MEETING_LIST.TABLE.COLUMNS.URL',
  },
  {
    id: 'date',
    title: 'MEETING_LIST.TABLE.COLUMNS.DATE',
  },
  {
    id: 'startTime',
    title: 'MEETING_LIST.TABLE.COLUMNS.START_TIME',
  },
  {
    id: 'guestCount',
    title: 'MEETING_LIST.TABLE.COLUMNS.GUESTS',
  },
  {
    id: 'privacyLevel',
    title: 'MEETING_LIST.TABLE.COLUMNS.VISIBILITY',
  },
  {
    id: 'actions',
    stickyEnd: true,
    title: 'MEETING_LIST.TABLE.COLUMNS.ACTIONS',
  },
];

@Injectable()
export class UpcomingDataSourceService
  implements PaginatedAPIInterface<UpcomingConversationRow, GalaxyFilterInterface, void>
{
  private readonly alertService = inject(SnackbarService);
  private readonly namespace$ = inject(MEETING_ANALYSIS_NAMESPACE_INJECTION_TOKEN$);
  private readonly userId$ = inject(MEETING_ANALYSIS_USER_ID_INJECTION_TOKEN$);
  get(
    request: PagedListRequestInterface<GalaxyFilterInterface, void>,
  ): Observable<PagedResponseInterface<UpcomingConversationRow>> {
    return combineLatest([this.namespace$, this.userId$]).pipe(
      take(1),
      switchMap(([namespace, userId]) => {
        return listMeetingBots$({
          filters: {
            namespace: namespace,
            eventStartTimeAfter: new Date(),
            userId: userId,
          },
          searchTerm: request.searchOptions?.text || '',
          pagingOptions: {
            pageSize: request.pagingOptions?.pageSize || 25,
            cursor: request.pagingOptions?.cursor || '',
          },
        }).pipe(
            catchError(() => {
              this.alertService.openErrorSnack('MEETING_LIST.ERROR_LOADING_MEETINGS');
              return of(null);
            }),
          );
      }),
      map((response) => {
        return {
          data: (response?.meetingBots || []).map((meetingBot) => this.convertMeetingBotToRow(meetingBot)),
          pagingMetadata: {
            nextCursor: response?.metadata?.nextCursor || '',
            hasMore: response?.metadata?.hasMore || false,
            totalResults: response?.metadata?.totalResults || 0,
          },
        };
      }),
    );
  }

  private convertMeetingBotToRow(meetingBot: MeetingBot): UpcomingConversationRow {
    const meetingDate = new Date(meetingBot.eventStartTime);

    return {
      id: meetingBot.id || '',
      namespace: meetingBot.namespace || '',
      meetingDate: meetingDate,
      title: meetingBot.eventTitle || '',
      url: meetingBot.meetingUrl || '',
      date: meetingDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }),
      startTime: meetingDate.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
      privacyLevel: meetingBot.privacyOverride?.privacyLevel || PrivacyLevel.PRIVACY_LEVEL_PRIVATE,
      guestCount: meetingBot.eventInvitees?.length || 0,
      overriddenPrivacy: meetingBot.privacyOverride || null,
    };
  }

  // listObjectFilters will return the list of filters that can be applied. They should be filtered by the searchTerm
  listObjectFilters(_: string): Observable<GalaxyFilterDefinitionInterface[]> {
    return of(UpcomingConversationFilters);
  }

  // getFieldOptions will return the list of options for a given fieldId.
  // This allows you to autocomplete a filter option for a user.
  getFieldOptions?(_fieldId: string, _search: string): Observable<string[]> {
    return of([] as string[]);
  }

  // getInitialAppliedFilters will return the default list of filters that should be displayed.
  // This allows you to have an initial filter state
  getInitialAppliedFilters?(): Promise<GalaxyFilterInterface[]> {
    return Promise.resolve([] as GalaxyFilterInterface[]);
  }

  setInitialAppliedFilters?(_filters: GalaxyFilterInterface[]): void {
    return;
  }

  // getFilterInputOverrides?(): Observable<GalaxyFilterInputOverride[]>;
  // This allows you to override the search box for any field
  // The filter input implementation will still have to abide by the operators allowed for that filter
  getFilterInputOverrides?(): Observable<GalaxyFilterInputOverride[]> {
    return of([] as GalaxyFilterInputOverride[]);
  }
}
